import * as React from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ratingsService } from '@/services/ratingsService';
import { useToast } from '@/context/ToastContext';
import { queryKeys } from '@/libs/queryKeys';
import type { MessageRating, RateMessageRequest, SubmitFeedbackRequest } from '@/types/ratings';
import type { FeedbackVariant } from '@/components/feedback/feedbackOptions';
import type { Message, ChatHistoryResponse } from '@/types';
import { useChatStore } from '@/store/chatStore';

interface UseMessageLikeReturn {
  rating: MessageRating;
  isLoading: boolean;
  handleRate: (type: 'like' | 'dislike' | 'super_like') => void;
  handleSubmitFeedback: (
    variant: FeedbackVariant,
    selectedOptions: string[],
    additionalText: string
  ) => void;
  isFeedbackLoading: boolean;
  checkIsFirstRatingForEcho: () => boolean;
}

const DEFAULT_RATING: MessageRating = {
  is_liked: false,
  is_disliked: false,
  is_super_liked: false,
};

/**
 * Per-message rating (like / dislike / super-like) with optimistic UI + tri-state toggle.
 * Submits via the ratings API and surfaces "first rating for this echo" via
 * `checkIsFirstRatingForEcho()` so callers can auto-open the feedback modal.
 */
export const useMessageLike = (
  messageId: string,
  isAiMessage: boolean,
  echoId?: string,
  initialRating?: MessageRating
): UseMessageLikeReturn => {
  const [localRating, setLocalRating] = React.useState<MessageRating>(
    initialRating || DEFAULT_RATING
  );
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  // Sync local state when prop updates (e.g. from cache refresh)
  React.useEffect(() => {
    if (initialRating) {
      setLocalRating(initialRating);
    }
  }, [initialRating]);

  const mutation = useMutation({
    mutationFn: (data: RateMessageRequest) => ratingsService.rateMessage(data),
    onMutate: async (newRatingVars) => {
      // 1. Optimistic Local Update
      const previousRating = localRating;
      setLocalRating(newRatingVars);

      if (!echoId) return { previousRating };

      const activeMessages = useChatStore.getState().getActiveMessages(echoId);
      useChatStore.getState().setActiveMessages(
        echoId,
        activeMessages.map((message) =>
          message.id === messageId || message.serverMessageId === messageId
            ? { ...message, rating: newRatingVars }
            : message
        )
      );

      // 2. Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.chat.history(echoId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.chat.lastMessage(echoId) });

      // 3. Snapshot previous data
      const previousHistory = queryClient.getQueryData(queryKeys.chat.history(echoId));
      const previousLastMessage = queryClient.getQueryData(queryKeys.chat.lastMessage(echoId));

      // 4. Optimistic Cache Update: History (Infinite Query)
      queryClient.setQueryData<InfiniteData<ChatHistoryResponse>>(
        queryKeys.chat.history(echoId),
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              messages: page.messages.map((msg) => {
                if (msg.id === messageId) {
                  return { ...msg, rating: newRatingVars };
                }
                return msg;
              }),
            })),
          };
        }
      );

      // 5. Optimistic Cache Update: Last Message
      queryClient.setQueryData(queryKeys.chat.lastMessage(echoId), (old: Message | null) => {
        if (old && old.id === messageId) {
          return { ...old, rating: newRatingVars };
        }
        return old;
      });

      return { previousRating, previousHistory, previousLastMessage };
    },
    onError: (_err, _newRating, context) => {
      // Rollback local state
      if (context?.previousRating) setLocalRating(context.previousRating);

      // Rollback cache
      if (echoId && context) {
        const activeMessages = useChatStore.getState().getActiveMessages(echoId);
        useChatStore.getState().setActiveMessages(
          echoId,
          activeMessages.map((message) =>
            message.id === messageId || message.serverMessageId === messageId
              ? { ...message, rating: context.previousRating }
              : message
          )
        );
        queryClient.setQueryData(queryKeys.chat.history(echoId), context.previousHistory);
        queryClient.setQueryData(queryKeys.chat.lastMessage(echoId), context.previousLastMessage);
      }

      addToast({
        title: 'Action Failed',
        subtitle: 'Could not update rating.',
        type: 'error',
      });
    },
    onSettled: () => {
      if (echoId) {
        // Refetch stats to ensure counts are correct
        queryClient.invalidateQueries({ queryKey: queryKeys.stats.echo(echoId) });
      }
    },
  });

  const handleRate = React.useCallback(
    (type: 'like' | 'dislike' | 'super_like') => {
      if (!isAiMessage) return;

      const newRating = { ...localRating };

      // Toggle Logic
      switch (type) {
        case 'like':
          newRating.is_liked = !newRating.is_liked;
          if (newRating.is_liked) {
            newRating.is_disliked = false;
            newRating.is_super_liked = false;
          }
          break;
        case 'super_like':
          newRating.is_super_liked = !newRating.is_super_liked;
          if (newRating.is_super_liked) {
            newRating.is_disliked = false;
            newRating.is_liked = false;
          }
          break;
        case 'dislike':
          newRating.is_disliked = !newRating.is_disliked;
          if (newRating.is_disliked) {
            newRating.is_liked = false;
            newRating.is_super_liked = false;
          }
          break;
        default:
          break;
      }

      mutation.mutate({
        message_id: messageId,
        ...newRating,
      });
    },
    [isAiMessage, localRating, messageId, mutation]
  );

  const checkIsFirstRatingForEcho = React.useCallback((): boolean => {
    if (!echoId) return true;
    const data = queryClient.getQueryData<InfiniteData<ChatHistoryResponse>>(
      queryKeys.chat.history(echoId)
    );
    if (!data) return true;
    return !data.pages.some((page) =>
      page.messages.some(
        (msg) =>
          msg.rating && (msg.rating.is_liked || msg.rating.is_disliked || msg.rating.is_super_liked)
      )
    );
  }, [echoId, queryClient]);

  const feedbackMutation = useMutation({
    mutationFn: (data: SubmitFeedbackRequest) => ratingsService.submitFeedback(data),
    onSuccess: () => {
      addToast({
        title: 'Feedback Sent',
        subtitle: 'Thank you for your feedback!',
        type: 'success',
      });
    },
    onError: () => {
      addToast({
        title: 'Feedback Failed',
        subtitle: 'Could not submit feedback. Please try again.',
        type: 'error',
      });
    },
  });

  const handleSubmitFeedback = React.useCallback(
    (variant: FeedbackVariant, selectedOptions: string[], additionalText: string) => {
      feedbackMutation.mutate({
        message_id: messageId,
        variant,
        selected_options: selectedOptions,
        ...(additionalText ? { additional_text: additionalText } : {}),
      });
    },
    [feedbackMutation, messageId]
  );

  return {
    rating: localRating,
    isLoading: mutation.isPending,
    handleRate,
    handleSubmitFeedback,
    isFeedbackLoading: feedbackMutation.isPending,
    checkIsFirstRatingForEcho,
  };
};
